import { Button, Typography } from 'omakase';
import {
  ConsumerPurchaseSummaryFragment,
  useDeleteWaitlistRequestMutation,
  useGetActiveWaitlistEntriesQuery,
  usePatronReservationHistoryLazyQuery,
  useReservationHistoryCountQuery,
} from 'generated/graphql';
import { FunctionComponent, useEffect, useState } from 'react';
import { core } from '@tock/coretypes';
import { gql } from '@apollo/client';
import { useHistory, useLocation } from 'react-router-dom';
import { usePrevious } from '@tock/hooks';
import Anchor from 'components/Anchor';
import LoadingComponent from '@tock/shared/components/LoadingComponent';
import ProfileListSideNav from '../ProfileListSideNav';
import ReservationHistorySection from 'components/profile/reservations/ReservationHistorySection';
import ResponsiveCallToAction from 'components/profile/ResponsiveCallToAction';
import WaitlistDeleteConfirmationModal, {
  WAITLIST_DELETE_CONFIRMATION_MODAL_FRAGMENT,
} from 'components/profile/WaitlistDeleteConfirmationModal';
import WaitlistPreviewCard, {
  WAITLIST_PREVIEW_CARD_FRAGMENT,
} from 'components/profile/WaitlistPreviewCard';

const RESERVATION_HISTORY_LIMIT = 30;
const OFFSET = 0;
interface Props {
  patron: core.IPatron;
}

gql`
  query PatronReservationHistory($offset: Int!, $limit: Int!, $selection: String!) {
    purchases(offset: $offset, limit: $limit, selection: $selection) {
      id
      ...ConsumerPurchaseSummary
    }
  }

  fragment ConsumerPurchaseSummary on ConsumerPurchaseSummary {
    business {
      domainName
      id
      profileImages {
        altText
        backingUrl
        dominantColor
        id
        imageUrl
      }
      name
    }
    cancelledOrRefunded
    city
    country
    dinerPatron {
      email
      firstName
      lastName
      id
    }
    eligibleForFeedback
    visitFiveStarRating
    firstTransferredTo {
      id
    }
    id
    ownerPatron {
      email
      firstName
      lastName
      id
    }
    ticketCount
    ticketDateTime
    ticketType {
      deliveryServiceProvider
      descriptiveVariety
      id
      name
      reserveShippingTime
      singleUnitQuantity
      variety
    }
  }
`;

gql`
  query ReservationHistoryCount {
    reservationHistoryCount {
      patron {
        id
      }
      cancelledBookingsCount
      pastBookingsCount
      upComingBookingsCount
    }
  }
`;

const FETCH_WAITLIST_ENTRIES = gql`
  query GetActiveWaitlistEntries {
    activeWaitlistsForPatron {
      id
      ...WaitlistPreviewCard
      ...WaitlistDeleteConfirmationModal
    }
  }
  ${WAITLIST_PREVIEW_CARD_FRAGMENT}
  ${WAITLIST_DELETE_CONFIRMATION_MODAL_FRAGMENT}
`;

gql`
  mutation DeleteWaitlistRequest($id: Int!) {
    deleteWaitlistRequest(id: $id)
  }
`;

const ReservationHistory = ({ patron }: Props) => {
  const { pathname } = useLocation();
  const history = useHistory();
  const [selectedSection, setSelectedSection] = useState<
    'UPCOMING' | 'WAITLIST' | 'PAST' | 'CANCELLED'
  >('UPCOMING');
  const [reservationHistoryLimit, setReservationHistoryLimit] =
    useState<number>(RESERVATION_HISTORY_LIMIT);
  const [reservationHistoryOffset, setReservationHistoryOffset] = useState<number>(OFFSET);
  const previousSelectedSection = usePrevious(selectedSection);
  const [getReservationHistory, { data, loading, error }] = usePatronReservationHistoryLazyQuery();
  const reservationHistory = data?.purchases ?? [];
  const { data: reservationHistoryCountData } = useReservationHistoryCountQuery();
  const [localReservationHistory, setLocalReservationHistory] =
    useState<ConsumerPurchaseSummaryFragment[]>(reservationHistory);
  const { data: activeWaitlistEntries } = useGetActiveWaitlistEntriesQuery();
  const [deleteWaitListRequest, { error: deleteWaitlistError }] =
    useDeleteWaitlistRequestMutation();

  const [waitlistIdToDelete, setWaitlistIdToDelete] = useState<number | undefined>(undefined);

  const onDelete = async (id: number): Promise<void> => {
    await deleteWaitListRequest({
      variables: { id },
      refetchQueries: [{ query: FETCH_WAITLIST_ENTRIES }],
    });
    if (deleteWaitlistError) {
      throw new Error('rejected');
    }
  };

  useEffect(() => {
    if (selectedSection !== previousSelectedSection) {
      /* When you change selection, set the next offset to 0.  You will retrieve the first page of results.
        Pressing Load more will increase the offset for the current selection.
        Start at 0 however for a new selection.
      */
      setReservationHistoryOffset(0);
    }
  }, [selectedSection, previousSelectedSection]);

  useEffect(() => {
    getReservationHistory({
      variables: {
        offset: reservationHistoryOffset,
        limit: reservationHistoryLimit,
        selection: selectedSection,
      },
    });
  }, [getReservationHistory, reservationHistoryOffset, reservationHistoryLimit, selectedSection]);

  useEffect(() => {
    if (pathname.includes('/upcoming')) {
      setSelectedSection('UPCOMING');
      setReservationHistoryLimit(RESERVATION_HISTORY_LIMIT);
      setLocalReservationHistory([]);
      return;
    }

    if (pathname.includes('/waitlist')) {
      setSelectedSection('WAITLIST');
      setReservationHistoryLimit(RESERVATION_HISTORY_LIMIT);
      setLocalReservationHistory([]);
      return;
    }

    if (pathname.includes('/past')) {
      setSelectedSection('PAST');
      setReservationHistoryLimit(RESERVATION_HISTORY_LIMIT);
      setReservationHistoryOffset(OFFSET);
      setLocalReservationHistory([]);
      return;
    }

    if (pathname.includes('/canceled')) {
      setSelectedSection('CANCELLED');
      setReservationHistoryLimit(RESERVATION_HISTORY_LIMIT);
      setReservationHistoryOffset(OFFSET);
      setLocalReservationHistory([]);
      return;
    }

    // catch-all fallback
    history.replace('/profile/reservations/upcoming');
  }, [history, pathname]);

  const handleLoadMoreReservations = async () => {
    setLocalReservationHistory(purchaseHistory);
    setReservationHistoryOffset(reservationHistoryOffset + RESERVATION_HISTORY_LIMIT);
  };

  const displayLoadMore = () => {
    if (error) {
      return false;
    }
    if (purchaseHistory && reservationHistoryCountData?.reservationHistoryCount && !loading) {
      if (
        selectedSection === 'UPCOMING' &&
        purchaseHistory.length <
          reservationHistoryCountData?.reservationHistoryCount.upComingBookingsCount
      ) {
        return true;
      } else if (
        selectedSection === 'PAST' &&
        purchaseHistory.length <
          reservationHistoryCountData?.reservationHistoryCount.pastBookingsCount
      ) {
        return true;
      } else if (
        selectedSection === 'CANCELLED' &&
        purchaseHistory.length <
          reservationHistoryCountData?.reservationHistoryCount.cancelledBookingsCount
      ) {
        return true;
      } else {
        return false;
      }
    }
    return false;
  };
  let purchaseHistory: ConsumerPurchaseSummaryFragment[] = [];
  purchaseHistory = localReservationHistory?.concat(
    reservationHistory ?? []
  ) as ConsumerPurchaseSummaryFragment[];

  const showManageReservation = selectedSection === 'UPCOMING' ? true : false;
  const emptyState =
    selectedSection === 'UPCOMING' ? (
      <UpcomingBookingsEmptyState />
    ) : (
      <EmptyState
        title="No past reservations"
        text={
          'You have no past reservations. After you are done enjoying your reservations, ' +
          ' they will show up here.'
        }
      />
    );

  const getSection = () => {
    if (selectedSection === 'WAITLIST') {
      return (
        <>
          {!activeWaitlistEntries?.activeWaitlistsForPatron?.length ? (
            <EmptyState
              title="No Notify requests"
              text="Find your next culinary experience. Start by searching reservations and events near you."
            />
          ) : (
            <>
              <WaitlistDeleteConfirmationModal
                isOpen={!!waitlistIdToDelete}
                onClose={() => setWaitlistIdToDelete(undefined)}
                id={waitlistIdToDelete}
                onDelete={onDelete}
                data={activeWaitlistEntries?.activeWaitlistsForPatron.find(
                  (entry) => entry.id === waitlistIdToDelete
                )}
              />

              {activeWaitlistEntries?.activeWaitlistsForPatron.map((entry) => (
                <WaitlistPreviewCard
                  key={entry.id}
                  data={entry}
                  onClickDelete={setWaitlistIdToDelete}
                />
              ))}
            </>
          )}
        </>
      );
    }

    return (
      <LoadingComponent isLoading={loading}>
        <ReservationHistorySection
          patron={patron}
          emptyState={emptyState}
          errorState={error && <ServerErrorState />}
          purchaseHistory={purchaseHistory}
          showManageReservation={showManageReservation}
          isSelected={selectedSection !== null}
        />

        {displayLoadMore() && (
          <Button
            size="large"
            variant="basic"
            onClick={handleLoadMoreReservations}
            disabled={loading}
            fullWidth
          >
            Load More
          </Button>
        )}
      </LoadingComponent>
    );
  };

  const navItems = [
    {
      id: 'UPCOMING',
      text: 'Upcoming',
      to: '/profile/reservations/upcoming',
    },
    {
      id: 'WAITLIST',
      text: 'Notify',
      to: '/profile/reservations/waitlist',
    },
    {
      id: 'PAST',
      text: 'Past',
      to: '/profile/reservations/past',
    },
  ];

  if (
    reservationHistoryCountData?.reservationHistoryCount &&
    reservationHistoryCountData?.reservationHistoryCount?.cancelledBookingsCount > 0
  ) {
    navItems.push({
      id: 'CANCELLED',
      text: 'Canceled',
      to: '/profile/reservations/canceled',
    });
  }

  return <ProfileListSideNav navItems={navItems}>{getSection()}</ProfileListSideNav>;
};

const UpcomingBookingsEmptyState: FunctionComponent = () => (
  <div className="ReservationsPage-emptyState-container">
    <ResponsiveCallToAction
      title={<Typography variant="heading03">No upcoming reservations</Typography>}
      bodyText={
        <Typography variant="body02">
          You have no upcoming reservations booked. Use Tock to find your next culinary experience.
          Start by exploring reservations and events near you.
        </Typography>
      }
      action={
        <Button href="/" component={Anchor} size="large" variant="basic" title="Explore now">
          Explore now
        </Button>
      }
      isTitle={false}
      imageUrl="https://tock-public.imgix.net/images-profile/tock-upcoming-reservations-nc.jpg"
      altText="Good Spirits & Company in Springfield, MO interior with bar seating and tables"
    />
  </div>
);

const ServerErrorState: FunctionComponent = () => (
  <div className="ReservationsPage-emptyState-container">
    <ResponsiveCallToAction
      title={
        <Typography variant="heading03">
          There was a problem retrieving your reservations
        </Typography>
      }
      bodyText={
        <Typography variant="body02">
          We had problems retrieving your reservations. Please reload the page to try again, or
          visit this page again later to see if the problem has been resolved!
        </Typography>
      }
      action={
        <Button href="/" component={Anchor} size="large" variant="basic" title="Explore now">
          Explore now
        </Button>
      }
      isTitle={false}
      imageUrl="https://storage.googleapis.com/tock-public-assets/images-profile/adventurous.png"
      altText="Food being prepared"
    />
  </div>
);

interface EmptyStateProps {
  title: string;
  text: string;
}

const EmptyState: FunctionComponent<EmptyStateProps> = ({ title, text }) => {
  return (
    <div className="ReservationsPage-emptyState-container ReservationHistory-emptyState-basicText">
      <Typography variant="body01Strong" mb={1}>
        {title}
      </Typography>
      <Typography variant="body02" mb={4}>
        {text}
      </Typography>
      <Button href="/" component={Anchor} size="large" variant="basic" title="Explore now">
        Explore now
      </Button>
    </div>
  );
};

export default ReservationHistory;
